import pick from 'object.pick';

import PPFActions from 'src/dispatcher';

import flowStore from 'src/stores/flowStore';
import registrationStore from 'src/stores/registrationStore';
import questionStore from 'src/stores/questionStore';

import userService from 'src/libs/userService';
import appConfig from 'src/utils/appConfig';
import registrationEventHandler from 'src/utils/events/registrationEventHandler';
import { pricesURL } from '@lessonnine/babbel-urls.js';
import { getReturnToURLFromParams } from 'src/router/queryString';
import InputView from './view';

const mapStateToRegistrationPartials = () => {
  const { partials, options, continueButtonLabel } = flowStore.getActiveNode();
  const { locale } = appConfig.serialize();

  return partials.map((inputName) => ({
    locale,
    options,
    continueButtonLabel,
    inputName,
    value: registrationStore.getUserData()[inputName],
    error: registrationStore.getUserErrors()[inputName]
  }));
};

const mapStateToTemplateScope = () => {
  const { type: nodeType, title, tagline, continueButtonLabel, variables, options } = flowStore.getActiveNode();
  const templateScope = Object.assign({ title, tagline, continueButtonLabel, nodeType }, variables);

  if (!Object.keys(options).length) return templateScope;

  const learningLanguage = appConfig.getLanguageTranslation();
  const { firstname } = registrationStore.getUserData();
  return Object.assign(templateScope, { options, learningLanguage, firstname });
};

const mapStateToRenderProps = () => ({
  registrationPartials: mapStateToRegistrationPartials(),
  templateScope: mapStateToTemplateScope()
});

const buildPostVerificationRedirectUrl = () => {
  // email link can be configured via return_to query parameter
  // defaults to pricesURL e.g. de.babbel.cn/preise/<learn_language>
  const postVerificationUrl = getReturnToURLFromParams() || pricesURL(appConfig.getLearningLanguage());
  const answerQuery = questionStore.getFilteredAnswersForQuery();
  if (answerQuery.length > 0) {
    const [url, query] = postVerificationUrl.split('?');
    const newUrl = query ? `${url}?${query}&${answerQuery}` : `${url}?${answerQuery}`;
    return encodeURIComponent(newUrl);
  }
  return encodeURIComponent(postVerificationUrl);
};

class InputViewController {
  constructor() {
    this.view = new InputView();
    this.onInputUpdate = this.onInputUpdate.bind(this);
    this.onUserCreationSuccess = this.onUserCreationSuccess.bind(this);
    this.onUserCreationFailure = this.onUserCreationFailure.bind(this);

    this.view.addCompletedListener(this.onInputCompleted.bind(this));
    this.view.addChangeListener(this.onInputUpdate);
  }

  destroy() {
    this.view.removeCompletedListener(this.onInputCompleted);
    this.view.removeChangeListener(this.onInputUpdate);
  }

  onInputUpdate(payload) {
    PPFActions.fireInputStoreDataAction(payload);
  }

  onStateChange() {
    this.view.render(mapStateToRenderProps());
  }

  onUserCreationSuccess({ user, userCreated }) {
    this.view.destroy();

    if (userCreated) {
      registrationEventHandler.trackSuccessRegistration(user.uuid);
      PPFActions.fireInputRegistrationCompletedAction();
    }

    PPFActions.fireInputCompletedAction({ user });
  }

  onUserCreationFailure({ user, error }) {
    this.view.destroy();
    const { partials } = flowStore.getActiveNode();
    const relevantErrors = pick(error, partials);
    const noRelevantErrorsExist = Object.keys(relevantErrors).length === 0;

    if (noRelevantErrorsExist) {
      PPFActions.fireInputCompletedAction({ user });
    } else {
      registrationEventHandler.trackInvalidInput(relevantErrors);
      PPFActions.fireInputValidationFailedAction({ user, error });
    }
  }

  onInputCompleted(user) {
    const newUser = {
      ...registrationStore.getUserData(),
      ...user,
      learn_language_alpha3: appConfig.getLearningLanguage()
    };
    const registrationOptions = registrationStore.getRegistrationOptions();
    registrationOptions.post_verification_redirect_url = buildPostVerificationRedirectUrl();

    return userService.create(newUser, registrationOptions)
      .then(this.onUserCreationSuccess, this.onUserCreationFailure);
  }
}

export default InputViewController;
